import React, { useEffect } from 'react';
import { UserData } from '../types';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';
import { calculateAge } from '../utils/calculator';
import { Sun, Moon, BatteryCharging, Sunrise, Percent, ArrowRight } from 'lucide-react';
import { DEFAULT_LIFE_EXPECTANCY } from '../constants';
import progressData from '../src/content/progress.json';

interface LifeProgressProps {
  userData: UserData;
  setUserData: (data: UserData) => void;
}

const LifeProgress: React.FC<LifeProgressProps> = ({ userData, setUserData }) => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'Life Progress Counter - Age & Life Expectancy Tracker | ZodiAge',
    description: 'Track your life progress with our cosmic age counter. See how much of your life you have lived with an interactive life expectancy visualizer and daily breakdown.',
    ogImage: 'https://zodiage.com/logo.png'
  });

  const hasBirthDate = !!userData.birthDate;

  // Defaults or Calculations
  let percentLived = 0;
  let ageInYears = 0;
  
  if (hasBirthDate && userData.birthDate) {
    const age = calculateAge(userData.birthDate);
    ageInYears = age.years + (age.months / 12) + (age.days / 365);
    percentLived = Math.min(100, Math.max(0, (ageInYears / userData.lifeExpectancy) * 100));
  }
  
  // Auto-adjust if age exceeds expectancy
  useEffect(() => {
    if (ageInYears > userData.lifeExpectancy) {
        setUserData({ ...userData, lifeExpectancy: Math.floor(ageInYears) + 5 });
    }
  }, [ageInYears, userData.lifeExpectancy, userData, setUserData]);

  const handleExpectancyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, lifeExpectancy: parseFloat(e.target.value) });
  };

  // Solar Cycle Calc
  const now = new Date();
  const currentHour = now.getHours() + (now.getMinutes()/60);
  const dayProgress = (currentHour / 24) * 100;

  return (
    <div className="space-y-12 animate-in fade-in duration-700">

            {/* Section 1: Life Expectancy (Only shows if date is set) */}
      {hasBirthDate ? (
        <div id="life-progress-section" className="bg-cosmic-800/50 p-6 rounded-2xl border border-cosmic-700 shadow-lg">
            <h2 className="text-2xl font-display text-white mb-6 flex items-center gap-3">
            <BatteryCharging className="text-green-400" /> Life Progress
            </h2>
            
            <div className="mb-8 relative h-12 bg-cosmic-900 rounded-full overflow-hidden border border-cosmic-700 shadow-inner">
                {/* ZodiAge golden color */}
                <div 
                    className="h-full transition-all duration-1000 ease-out flex items-center justify-end px-4"
                    style={{ 
                        width: `${percentLived}%`,
                        background: '#FFC82C'
                    }}
                >
                    <span className="text-black font-bold text-shadow drop-shadow-md whitespace-nowrap">
                        {percentLived.toFixed(1)}% Lived
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-center mb-6">
                <div className="p-4 bg-cosmic-900/50 rounded-xl">
                    <div className="text-3xl font-bold text-white">{ageInYears.toFixed(2)}</div>
                    <div className="text-xs text-cosmic-400 uppercase">Years Gone</div>
                </div>
                <div className="p-4 bg-cosmic-900/50 rounded-xl">
                    {/* ZodiAge golden color */}
                    <div className="text-3xl font-bold" style={{ color: '#FFC82C' }}>{(userData.lifeExpectancy - ageInYears).toFixed(2)}</div>
                    <div className="text-xs text-cosmic-400 uppercase">Years Remaining</div>
                </div>
            </div>

            <div className="space-y-2">
                <label className="text-sm text-cosmic-300">Adjust Target Life Expectancy (Global Avg: {DEFAULT_LIFE_EXPECTANCY})</label>
                <input 
                    type="range" 
                    min="50" 
                    max="150" 
                    step="0.1"
                    value={userData.lifeExpectancy}
                    onChange={handleExpectancyChange}
                    className="w-full h-2 bg-cosmic-900 rounded-lg appearance-none cursor-pointer"
                    style={{
                        accentColor: '#FFC82C',
                        background: `linear-gradient(to right, #FFC82C 0%, #FFC82C ${((userData.lifeExpectancy - 50) / (150 - 50)) * 100}%, transparent ${((userData.lifeExpectancy - 50) / (150 - 50)) * 100}%, transparent 100%)`
                    }}
                />
                <div className="text-right font-mono" style={{ color: '#FFC82C' }}>{userData.lifeExpectancy} Years</div>
            </div>

            {/* Explanation */}
            <div className="mt-6 p-4 bg-cosmic-900/50 rounded-xl border border-cosmic-700/50">
                <p className="text-sm text-cosmic-400 leading-relaxed">
                    <span className="text-cosmic-300 font-medium">What is Life Progress?</span> This visualization shows how much of your expected lifespan you've already lived based on your birth date. 
                    The golden progress bar fills as time passes, while the stats below display your years lived and estimated years remaining. Use the slider to adjust your target life expectancy.
                </p>
            </div>
        </div>
      ) : (
        <div className="bg-cosmic-800/50 p-8 rounded-2xl border border-cosmic-700 text-center shadow-lg">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Life Progress Visualizer</h1>
            <p className="text-gray-400 mb-6">Enter your birth date to unlock your personal life progress and solar cycle visualizations.</p>
            <button
                onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-semibold px-6 py-3 rounded-full hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 transition-all inline-flex items-center gap-2 shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70"
            >
                Go to Calculator <ArrowRight size={18}/>
            </button>
        </div>
      )}

      {/* Educational Content — Always visible for crawlers */}
      <article className="max-w-3xl mx-auto mb-12">
          <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
              <div className="seo-content" dangerouslySetInnerHTML={{ __html: progressData.html }} />
          </div>
        <section className="bg-green-900/20 border border-green-700/30 rounded-2xl p-5 mt-8">
          <div className="flex items-start gap-3">
            <Percent size={20} className="text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-white text-sm mb-1">About Life Progress</h3>
              <p className="text-sm text-green-100 leading-relaxed">
                This is a philosophical reflection tool based on statistical averages. Life expectancy varies by
                individual factors including genetics, lifestyle, and healthcare access. Use for contemplation
                and motivation, not as a personal prediction.
              </p>
            </div>
          </div>

          {/* In-Article Ad for Life Progress Page */}
          <AdSpace type="in-article" slot="7716101506" className="my-8" />
        </section>
      </article>

      {/* Section 2: Symbolic Daily Cycle (Sunrise/Sunset) - Only shows if date is set */}
      {hasBirthDate && (
        <div className="bg-cosmic-800/50 p-6 rounded-2xl border border-cosmic-700 shadow-lg">
            <h2 className="text-2xl font-display text-white mb-6 flex items-center gap-3">
                <Sun className="text-yellow-400" /> Solar Cycle (Today)
            </h2>
            
            <div className="relative h-48 bg-cosmic-900 rounded-xl overflow-hidden border border-cosmic-700 flex items-end pb-0">
                {/* Sky Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-cosmic-800 via-cosmic-900 to-black opacity-80"></div>
                
                {/* Sun Path Arc Visualization (Simple CSS) */}
                <div className="absolute left-0 bottom-0 w-full h-full flex items-end justify-center pointer-events-none">
                    <div className="w-[120%] h-[200%] border-t border-dashed border-white/10 rounded-[50%] absolute -bottom-[100%]"></div>
                </div>

                {/* Moving Sun */}
                <div 
                    className="absolute w-10 h-10 bg-primary rounded-full shadow-[0_0_30px_#FFC82C] transition-all duration-1000 z-10"
                    style={{ 
                        left: `${dayProgress}%`,
                        bottom: `${Math.sin((dayProgress / 100) * Math.PI) * 75}%` // Simple parabolic arc
                    }}
                >
                    {/* Inner Sun Detail */}
                    <div className="absolute inset-0 bg-white/30 rounded-full blur-[2px]"></div>
                </div>
                
                {/* Horizon Line */}
                <div className="absolute bottom-0 w-full h-px bg-cosmic-500/50"></div>

                {/* Labels */}
                <div className="absolute bottom-4 left-6 text-xs text-cosmic-400 flex flex-col items-center gap-1">
                    <Sunrise size={16} className="text-yellow-100" /> 
                    <span>Sunrise (06:00)</span>
                </div>
                <div className="absolute bottom-4 right-6 text-xs text-cosmic-400 flex flex-col items-center gap-1">
                    <Moon size={16} className="text-blue-200" /> 
                    <span>Sunset (18:00)</span>
                </div>
            </div>

            <div className="mt-6 text-center text-sm text-cosmic-300">
                Current Time: <span className="text-white font-mono font-bold tracking-wider">{now.toLocaleTimeString()}</span>
            </div>

            {/* Explanation */}
            <div className="mt-6 p-4 bg-cosmic-900/50 rounded-xl border border-cosmic-700/50">
                <p className="text-sm text-cosmic-400 leading-relaxed">
                    <span className="text-cosmic-300 font-medium">What is the Solar Cycle?</span> This visualization tracks the sun's symbolic journey across the sky throughout your day. 
                    The sun rises at 6:00 AM, reaches its peak at noon, and sets at 6:00 PM. Watch the golden orb move along its arc as the day progresses — a reminder that each moment is part of a larger cosmic rhythm.
                </p>
            </div>
        </div>
      )}
    </div>
  );
};

export default LifeProgress;
